<template>
  <div class="tw:flex tw:flex-col tw:gap-1.5">
    <label class="tw:flex! tw:cursor-pointer tw:items-center tw:gap-2.5">
      <!-- the native input stays off-screen but carries state, keyboard and screen reader behaviour -->
      <span
        class="tw:relative tw:inline-flex tw:size-[18px] tw:shrink-0 tw:items-center tw:justify-center tw:rounded-sm tw:border tw:border-solid tw:shadow-[0_1px_2px_0_rgb(16_24_40_/_0.05)] tw:transition-[background-color,border-color,box-shadow] tw:duration-150 tw:ease-in-out tw:hover:tw:border-primary tw:focus-within:tw:shadow-[0_0_0_4px_rgb(0_122_255_/_0.10)]"
        :class="[
          // one background at a time, so checked never has to outrank unchecked in the compiled css
          model || indeterminate ? 'tw:border-primary tw:bg-primary' : 'tw:border-gray-300 tw:bg-white',
          hasErrors ? 'tw:border-danger' : '',
        ]">
        <input
          ref="inputRef"
          v-model="model"
          type="checkbox"
          class="tw:sr-only"
          :aria-invalid="hasErrors || undefined"
          :aria-describedby="hasErrors ? errorId : undefined"
          v-bind="$attrs" />
        <IconMinus v-if="indeterminate" size="14" stroke="2.5" class="tw:text-white" />
        <IconCheck v-else-if="model" size="14" stroke="2.5" class="tw:text-white" />
      </span>

      <span v-if="label || $slots.default" class="tw:text-14 tw:text-gray-700 tw:[text-wrap:pretty]">
        <!-- slot as well as prop so labels can contain links -->
        <slot>{{ label }}</slot>
      </span>
    </label>

    <span v-if="hasErrors" :id="errorId" class="tw:text-13 tw:text-danger">{{ firstError }}</span>
  </div>
</template>

<script setup lang="ts">
import { IconCheck, IconMinus } from '@tabler/icons-vue';
import { computed, useId, useTemplateRef, watchEffect } from 'vue';

interface Props {
  /** plain-text label; use the default slot instead when the label contains links */
  label?: string;
  /** one message or several, matching LeadInput */
  errors?: string | string[] | null;
  /** purely visual tri-state flag, independent of the checked model */
  indeterminate?: boolean;
}

const props = defineProps<Props>();
const model = defineModel<boolean>({ default: false });

defineOptions({ inheritAttrs: false });

/* ---------------------------------- STATE ---------------------------------- */
const errorId = `lead-checkbox-error-${useId()}`;
const inputRef = useTemplateRef<HTMLInputElement>('inputRef');

/* --------------------------------- COMPUTED -------------------------------- */
const firstError = computed(() => {
  if (!props.errors) return '';
  return Array.isArray(props.errors) ? props.errors[0] : props.errors;
});

const hasErrors = computed(() => firstError.value !== '');

/* --------------------------------- WATCHERS -------------------------------- */
// indeterminate has no HTML attribute equivalent, it can only be set as a DOM property
watchEffect(
  () => {
    if (inputRef.value) inputRef.value.indeterminate = Boolean(props.indeterminate);
  },
  { flush: 'post' }
);
</script>
